/* =========================================================================
   HOME PROGRESSION SURFACES (PLAN-progression.md §5.1 + §5.5)

     • "Level 12 · Acolyte" + the XP bar — always on home for a signed-in
       player, so a game never starts from a cold, level-less home.
     • "Continue your Game — Round 3 of 5 · $2,350 banked" — the server-saved
       session card. Tapping it runs the SAME primary play action (#startBtn),
       which resumes from the server; no separate launch path exists to drift.

   Guests, an un-deployed endpoint, an error or an empty answer all leave BOTH
   elements hidden — home renders exactly as it does today.

   Wiring: this module attaches itself (main.ts is owned elsewhere). It watches
   for the app shell to mount, then repaints whenever #screenHome becomes
   visible — the same moment main.ts's own home render seam fires. Callers may
   also invoke renderHomeProgress() directly after an XP award.
   ========================================================================= */
import { fmtCents } from '../engine';
import { isAuthenticated } from '../api';
import { ROUNDS } from '../data/packs';
import { currentIdentity, readSnapshot } from '../resumeGame';
import { cachedProgress, fetchActiveSession, fetchProgress, type ActiveSession, type GameProgress } from '../progressClient';
import { fmtXp, xpPct } from './xpMath';

function el(id: string): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.getElementById(id);
}

function setHidden(id: string, hidden: boolean): void {
  el(id)?.classList.toggle('hidden', hidden);
}

/* ------------------------------- pure copy ------------------------------- */

/** The badge line: "✦ Level 12". The ✦ chip framing (not a bare "Level 12"
    heading) keeps it reading as identity, not a position counter next to
    "Round 2 of 5". Rank titles were dropped here entirely (Eric 2026-08-16 —
    users didn't know what "Lamplighter" meant); titles now live only in the
    rank-up ceremony. PURE. */
export function badgeText(p: GameProgress): { level: string; rank: string; xp: string } {
  const lv = Math.max(1, Math.round(p.careerLevel));
  const xp =
    p.xpToNextLevel > 0
      ? `${fmtXp(p.xpIntoLevel)} / ${fmtXp(p.xpToNextLevel)} XP`
      : `${fmtXp(p.xpTotal)} XP`;
  return { level: `Level ${lv}`, rank: '', xp };
}

/** "Round 3 of 5 · $2,350 banked" (roundIndex is 0-based). PURE. */
export function continueText(s: ActiveSession, totalRounds: number = ROUNDS.length): string {
  const round = Math.min(Math.max(1, s.roundIndex + 1), Math.max(1, totalRounds));
  const parts = [`Round ${round} of ${totalRounds}`, `${fmtCents(s.bankCents)} banked`];
  if (s.hardMode) parts.push('Hard mode');
  return parts.join(' · ');
}

/* -------------------------------- painting -------------------------------- */

function paintProgress(p: GameProgress | null): void {
  const host = el('homeProgress');
  if (!host) return;
  if (!p) {
    host.classList.add('hidden');
    return;
  }
  const t = badgeText(p);
  const lvl = el('hpLevel');
  if (lvl) lvl.textContent = t.level;
  const rank = el('hpRank');
  if (rank) {
    rank.textContent = t.rank;
    rank.classList.toggle('hidden', !t.rank);
  }
  const xp = el('hpXp');
  if (xp) xp.textContent = t.xp;

  const pct = xpPct(p.xpIntoLevel, p.xpToNextLevel);
  const fill = el('hpBarFill');
  if (fill) fill.style.width = pct + '%';
  const bar = el('hpBar');
  if (bar) {
    bar.setAttribute('aria-valuenow', String(Math.round(pct)));
    bar.setAttribute('aria-label', `${t.level}${t.rank ? ', ' + t.rank : ''} — ${t.xp}`);
  }
  host.classList.remove('hidden');
}

function paintContinue(s: ActiveSession | null): void {
  const host = el('homeContinue');
  if (!host) return;
  // A local round-boundary snapshot already turns #startBtn into
  // "▶ Resume — Round N …" (resumeGame.renderResumeCta). Two resume controls on
  // one screen is noise, so the card yields to it.
  const localResume = (() => {
    try {
      return !!readSnapshot(currentIdentity());
    } catch {
      return false;
    }
  })();
  if (!s || localResume) {
    host.classList.add('hidden');
    return;
  }
  const line = el('homeContinueLine');
  if (line) line.textContent = continueText(s);
  host.classList.remove('hidden');
}

/** Repaint both home progression surfaces. Cheap and idempotent; safe to call
    whenever home is (re)shown. Never throws. */
export function renderHomeProgress(): void {
  const host = el('homeProgress');
  if (!host) return; // no DOM (tests) / older shell
  wireContinue(); // idempotent — also covers a re-rendered shell
  if (!isAuthenticated()) {
    setHidden('homeProgress', true);
    setHidden('homeContinue', true);
    return;
  }
  paintProgress(cachedProgress()); // instant paint from the last known values
  void fetchProgress()
    .then(paintProgress)
    .catch(() => {
      /* fail soft — the badge simply stays as it was */
    });
  void fetchActiveSession()
    .then(paintContinue)
    .catch(() => paintContinue(null));
}

/* -------------------------------- wiring -------------------------------- */

let wired = false;

function wireContinue(): void {
  const btn = el('homeContinue');
  if (!btn || btn.dataset.wired === '1') return;
  btn.dataset.wired = '1';
  btn.addEventListener('click', () => {
    // Route through the primary play CTA so there is exactly ONE launch path
    // (main.ts wires it to gameController.startOrResume, which resumes the
    // server session). Never a second, drifting entry point.
    el('startBtn')?.click();
  });
}

function attachTo(screen: HTMLElement): void {
  wireContinue();
  if (typeof MutationObserver === 'function') {
    new MutationObserver(() => {
      if (!screen.classList.contains('hidden')) renderHomeProgress();
    }).observe(screen, { attributes: true, attributeFilter: ['class'] });
  }
  if (!screen.classList.contains('hidden')) renderHomeProgress();
}

/** Attach the home render seam. Idempotent; waits for the app shell to mount
    (main.ts sets #app.innerHTML at boot, after every module has loaded). */
export function initHomeProgress(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;
  const now = document.getElementById('screenHome');
  if (now) {
    attachTo(now);
    return;
  }
  if (typeof MutationObserver !== 'function') return;
  const root = document.documentElement || document.body;
  if (!root) return;
  const waiting = new MutationObserver(() => {
    const screen = document.getElementById('screenHome');
    if (!screen) return;
    waiting.disconnect();
    attachTo(screen);
  });
  waiting.observe(root, { childList: true, subtree: true });
}
